/**
 * Composes every cross-cutting provider in the correct order (STEP
 * 5/6/8/9/11/12): error boundary (outermost, catches everything
 * below), TanStack Query, MUI theme, date-picker localization, and
 * the toast host. `App.tsx` renders only this + `AppRouter`.
 */
import type { JSX, ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Toaster } from 'react-hot-toast';
import { NotificationDialog } from '@components/notifications/NotificationDialog';
import { SystemStatusNotice } from '@components/notifications/SystemStatusNotice';
import { queryClient } from '@core/api/query-client';
import { ThemeModeProvider } from './ThemeModeProvider';
import { ErrorFallback } from '@components/feedback/ErrorFallback';
import { isDevelopment } from '@core/config/env';

export function AppProviders({ children }: { children: ReactNode }): JSX.Element {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onReset={() => window.location.replace('/')}>
      <QueryClientProvider client={queryClient}>
        <ThemeModeProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {children}
            <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
            {/* Every notification: centred, stays until the user clicks Noted (see NotificationDialog). The Toaster above only serves severities switched off in `ACKNOWLEDGE`. */}
            <NotificationDialog />
            {/* Opened last so it sits above everything: the server is being updated / is back / a new version was installed. */}
            <SystemStatusNotice />
            {isDevelopment && <ReactQueryDevtools initialIsOpen={false} />}
          </LocalizationProvider>
        </ThemeModeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
